import { useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { useSEO } from "../lib/seo";
import { Reveal } from "../lib/reveal";
import { CHANTIER_VIDEOS } from "../data/content";

const VideoCard = ({ video, index }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <Reveal delay={index * 0.08}>
      <article className="border border-black/10 bg-white" data-testid={`video-card-${index}`}>
        <div className="relative aspect-[4/5] overflow-hidden bg-black">
          {playing ? (
            <video
              src={video.src}
              controls
              autoPlay
              playsInline
              className="h-full w-full object-contain"
              data-testid={`video-player-${index}`}
            >
              Votre navigateur ne prend pas en charge la lecture vidéo.
            </video>
          ) : (
            <button
              onClick={() => setPlaying(true)}
              data-testid={`video-play-${index}`}
              aria-label={`Lire la vidéo : ${video.title}`}
              className="group relative block h-full w-full"
            >
              <img
                src={video.poster}
                alt={video.title}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
              />
              <span className="absolute inset-0 bg-black/20 transition-colors duration-300 group-hover:bg-black/35" />
              <span className="absolute inset-0 grid place-items-center">
                <span className="grid h-16 w-16 place-items-center rounded-full bg-bati-blue transition-colors duration-300 group-hover:bg-bati-sky">
                  <Play className="ml-1 h-6 w-6 fill-white text-white" />
                </span>
              </span>
            </button>
          )}
        </div>
        <div className="p-5">
          <h2 className="font-display text-lg font-bold leading-snug text-bati-ink">{video.title}</h2>
          <p className="mt-1 text-sm text-bati-ink/55">{video.subtitle}</p>
          <Link
            to={video.project}
            data-testid={`video-project-link-${index}`}
            className="group mt-3 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.12em] text-bati-blue transition-colors hover:text-bati-sky"
          >
            Voir la fiche chantier
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </article>
    </Reveal>
  );
};

const VideosPage = () => {
  useSEO(
    "Vidéos de chantiers — BÂTI 360 | Rénovation Paris & Île-de-France",
    "Regardez nos chantiers en vidéo : surélévation en ossature bois, couverture zinc, étanchéité de toiture-terrasse et rénovation complète, filmés par BÂTI 360 en Île-de-France."
  );

  return (
    <div className="pt-20 md:pt-24">
      <header className="border-b border-black/10 bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="mb-4 flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.22em] text-bati-sky">
              <span className="h-px w-10 bg-bati-sky" /> En immersion
            </p>
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-bati-ink sm:text-5xl lg:text-6xl">
              Nos chantiers en vidéo
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-bati-ink/60">
              Nos chantiers filmés sur le terrain : structure, toiture, étanchéité et finitions. Lecture à la demande — aucune vidéo ne démarre automatiquement.
            </p>
          </Reveal>
        </div>
      </header>

      <section className="bg-white py-14 lg:py-20" data-testid="videos-page">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {CHANTIER_VIDEOS.map((video, i) => (
              <VideoCard key={video.src} video={video} index={i} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VideosPage;
